import { useTranslation } from '@pypestream/translations';
import { useEffect, useRef, useState } from 'react';
import { proStudioUrqlGql } from '@pypestream/api-services';
import { dismissToasts } from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { resolveBobcatAccess } from '../utils/bobcat-flags';
import { publishToastCopy } from '../components/modals/publish-modal/publish-toast-copy';
import {
  buildPublishAttemptId,
  clearLocalPublishAttempt,
  LOCAL_PUBLISH_ATTEMPT_WAIT_MS,
  notifyPublishOutcome,
  PUBLISH_IN_PROGRESS_TOAST_ID,
  readLocalPublishAttempt,
  restorePublishUiState,
  subscribeLocalPublishAttempts,
} from '../utils/publish-status';
import {
  capitalizeWords,
  msUntilPublishLockExpiry,
  pickDeploymentChangedSince,
  PUBLISH_OUTCOME_GRACE_MS,
  readDeploymentAuthorName,
  readSolutionPublishStatus,
  trackPublishProgress,
} from '../utils';
import { selectOpenSolutionId } from '../utils/select-open-solution-id';
import { sendUserMessage, updateUserMessage } from '../xstate/app.xstate-utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { reportObserverFailure } from '../xstate/report-observer-failure';

/**
 * Everyone's view of a publish: the Publish button disabled while one runs, and a toast
 * that says who is publishing what, where, and which step it is on — then how it ended.
 *
 * Draft-only versioning (backend `docs/solution-versioning-redesign.md`): publishing
 * snapshots the draft onto a separate version slot and never touches the draft, so the
 * editor stays EDITABLE throughout — only the Publish button is disabled, for everyone,
 * so two publishes cannot run at once. Nothing here forces view mode any more.
 *
 * Two sources, deliberately:
 *
 * - the SOLUTION's status (`PUBLISHING` while the backend holds its publish lock) says a
 *   publish is running. It flips the instant a publish or republish is accepted, before
 *   the deployment row exists. The draft version's own status is useless for this now: it
 *   never changes — the slot is what goes PUBLISHING.
 * - the deployment rows (`ctx.solutionDeployments`, fed by `useSolutionDeployments`) say
 *   how the publish is going and how it ended. The status cannot: it returns to `ACTIVE`
 *   on failure exactly as on success.
 *
 * The solution status is still the fallback when there is no deployment row at all, so a
 * solution whose rows are unreadable still gets its button back.
 *
 * A lock that outlives the backend's timeout with the attempt unfinished is a pipeline that
 * hung (PSTUDIO-4333): a timer re-reads it at expiry, since a hung pipeline pushes no payload
 * that would, and the tab lifts the button lock and says the publish looks stuck.
 *
 * Mounted once at the router, keyed on the open solution.
 */
export function usePublishStatusWatcherForCoworkers(): void {
  const [t] = useTranslation();
  const { solutionId, deployments, projectUsers, currentUserId } =
    useProStudioCtxSelector((ctx) => ({
      // The same key `useSolutionDeployments` subscribes on.
      solutionId: selectOpenSolutionId(ctx),
      deployments: ctx.solutionDeployments,
      projectUsers: ctx.projectUsers,
      currentUserId: ctx.userInfo?.id,
    }));

  /**
   * The solution row as last delivered, as primitives so an identical payload does not
   * re-run the progress effect — and kept on a subscription error, so the lock still expires.
   */
  const [solutionStatus, setSolutionStatus] = useState<string | null>(null);
  const [lockStartedAt, setLockStartedAt] = useState<string | null>(null);
  /**
   * Bumped by the two timers — lock expiry and the post-release wait — so the progress effect
   * re-reads the clock when nothing is pushed.
   */
  const [clockTick, setClockTick] = useState(0);
  /** When this tab saw the attempt's lock released — starts the wait for its row. */
  const releasedAtRef = useRef<number | null>(null);

  /** True while this tab is showing the in-progress state, so it can announce the outcome. */
  const isShowingProgressRef = useRef(false);
  /** Last in-progress toast body, so a step change updates the toast instead of stacking one. */
  const progressTextRef = useRef<string | null>(null);
  /** The deployment row being followed — see `trackPublishProgress`. */
  const trackedDeploymentIdRef = useRef<string | undefined>(undefined);
  /**
   * `publishStartedAt` of the attempt in progress — it tells one attempt from the next when
   * there is no deployment row to deduplicate the announcement on.
   */
  const publishStartedAtRef = useRef<string | null>(null);
  /** The live lock's start, for the subscription error path, which runs outside render. */
  const lockStartedAtRef = useRef<string | null>(null);
  /**
   * Whether this tab saw the solution go PUBLISHING. Required for the no-deployment-row
   * fallback: without it, a stale `ACTIVE` (e.g. carried over as the open solution changes)
   * would read as a publish that just finished.
   */
  const sawPublishingRef = useRef(false);

  useEffect(() => {
    if (!solutionId) {
      setSolutionStatus(null);
      setLockStartedAt(null);
      return undefined;
    }

    const { subscribe } = proStudioUrqlGql.solutionPublishStatusSubscription({
      id: solutionId,
    });
    const { unsubscribe } = subscribe((subData) => {
      if (subData.error) {
        // PSTUDIO-4333 B5: no longer silent. The last known status is kept on purpose: a
        // known lock still expires on the timer, and the attempt's row still reports its
        // outcome. Only with NO lock seen — the optimistic window after confirming — is there
        // nothing left to end the attempt, so the button is released rather than held
        // forever. The backend's lock still refuses a second concurrent publish (CONFLICT).
        reportObserverFailure(subData.error, 'publish-watcher:status');
        if (
          !lockStartedAtRef.current &&
          !trackedDeploymentIdRef.current &&
          (isShowingProgressRef.current ||
            tableStore.solutionVersionPublishingInProgress)
        ) {
          isShowingProgressRef.current = false;
          progressTextRef.current = null;
          sawPublishingRef.current = false;
          restorePublishUiState();
        }
        return;
      }
      const row = subData.data?.tenancy_solutions_by_pk;
      if (row == null) return;
      if (typeof row.publishStartedAt === 'string') {
        publishStartedAtRef.current = row.publishStartedAt;
      }
      const liveLock =
        row.status === 'PUBLISHING' && typeof row.publishStartedAt === 'string'
          ? row.publishStartedAt
          : null;
      lockStartedAtRef.current = liveLock;
      setSolutionStatus(typeof row.status === 'string' ? row.status : null);
      setLockStartedAt(liveLock);
    });

    return () => {
      unsubscribe();
      setSolutionStatus(null);
      setLockStartedAt(null);
      lockStartedAtRef.current = null;
      releasedAtRef.current = null;
      const wasShowingProgress = isShowingProgressRef.current;
      isShowingProgressRef.current = false;
      progressTextRef.current = null;
      trackedDeploymentIdRef.current = undefined;
      publishStartedAtRef.current = null;
      sawPublishingRef.current = false;
      // PSTUDIO-3985: undo what the in-progress branch raised. The button lock and the
      // `autoClose: false` banner are global while this subscription is scoped to one
      // solution, so a solution the backend leaves stuck in PUBLISHING would otherwise
      // carry them onto every solution opened afterwards. The toast is dismissed only when
      // it was raised, because this runs on every navigation; it reads the store flag too
      // because `PublishModal.onPublish` raises both before this subscription has seen
      // PUBLISHING.
      const wasPublishing =
        wasShowingProgress || tableStore.solutionVersionPublishingInProgress;
      tableStore.setSolutionVersionPublishingInProgress(false);
      if (wasPublishing) {
        dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
      }
    };
  }, [solutionId]);

  // This tab's own publish is recorded outside React (`beginLocalPublishAttempt`); re-read
  // when it changes, since nothing in the context does when the backend accepts it.
  useEffect(
    () => subscribeLocalPublishAttempts(() => setClockTick((tick) => tick + 1)),
    []
  );

  // Re-read the lock when it expires: a hung pipeline pushes nothing that would. Timed off
  // `msUntilPublishLockExpiry`, which reads the offset-less start as UTC the same way the
  // expiry check does — a bare `Date.parse` here read it as local time.
  useEffect(() => {
    const remaining = msUntilPublishLockExpiry(lockStartedAt);
    if (remaining === null || remaining <= 0) return undefined;
    const timer = setTimeout(() => setClockTick((tick) => tick + 1), remaining);
    return () => clearTimeout(timer);
  }, [lockStartedAt]);

  useEffect(() => {
    const now = Date.now();
    const versionStatus = readSolutionPublishStatus(
      solutionStatus === null
        ? null
        : { status: solutionStatus, publishStartedAt: lockStartedAt },
      now
    );
    if (versionStatus === 'PUBLISHING') {
      sawPublishingRef.current = true;
      releasedAtRef.current = null;
    } else if (sawPublishingRef.current && releasedAtRef.current === null) {
      releasedAtRef.current = now;
    }

    // This tab's own publish, when the lock came and went between two polls and PUBLISHING
    // was never seen (PSTUDIO-4333). Once PUBLISHING is seen, the path below owns it.
    const localAttempt = readLocalPublishAttempt(solutionId);
    if (localAttempt && sawPublishingRef.current) {
      clearLocalPublishAttempt(solutionId);
    } else if (
      localAttempt?.acceptedAt != null &&
      !isShowingProgressRef.current
    ) {
      const ownRow = pickDeploymentChangedSince(
        deployments,
        localAttempt.baseline
      );
      if (ownRow) {
        // Hand it to the tracking below as though it had been seen running.
        trackedDeploymentIdRef.current = ownRow.id;
        isShowingProgressRef.current = true;
        clearLocalPublishAttempt(solutionId);
      } else if (
        now - localAttempt.acceptedAt >=
        LOCAL_PUBLISH_ATTEMPT_WAIT_MS
      ) {
        // No sign of it at all: nothing is known about how it went, so nothing is claimed,
        // but the button this tab raised is not left locked.
        clearLocalPublishAttempt(solutionId);
        restorePublishUiState();
        return undefined;
      } else {
        const timer = setTimeout(
          () => setClockTick((tick) => tick + 1),
          localAttempt.acceptedAt + LOCAL_PUBLISH_ATTEMPT_WAIT_MS - now
        );
        return () => clearTimeout(timer);
      }
    }

    const { phase, deployment, step, trackedDeploymentId } =
      trackPublishProgress({
        trackedDeploymentId: trackedDeploymentIdRef.current,
        versionStatus,
        // No version to prefer: the draft has no deployment rows of its own, and the slot
        // a publish runs on is only known from its row — the newest running one.
        solutionVersionId: null,
        deployments,
        publishStartedAt: publishStartedAtRef.current,
        lockStartedAt,
        releasedAt: releasedAtRef.current,
        now,
      });
    trackedDeploymentIdRef.current = trackedDeploymentId;
    const environment = capitalizeWords(deployment?.environment ?? '');
    const version = deployment?.versionName ?? '';
    const author = readDeploymentAuthorName(
      deployment?.deployedBy,
      projectUsers
    );
    const isSelf = Boolean(
      deployment?.deployedBy && deployment.deployedBy === currentUserId
    );

    // PSTUDIO-4635: Bobcat's shorter wording with its master flag on; production's otherwise.
    const copy = publishToastCopy(
      resolveBobcatAccess('fiveStageNav', tableStore.featureFlags) === 'allowed'
    );

    if (phase === 'in_progress') {
      // Named header when a coworker is publishing — that is the fact that explains why the
      // Publish button just went disabled. Falls back to the impersonal wording when the row (and so
      // the environment, version and author) is not there yet.
      const header =
        version && environment && author && !isSelf
          ? (t('pro-studio/navigation:publish_modal.toast.publishing_by', {
              defaultValue: '{{name}} is publishing {{version}} to {{env}}',
              name: author,
              version,
              env: environment,
            }) ?? undefined)
          : version && environment
            ? (t('pro-studio/navigation:publish_modal.toast.publishing_to', {
                defaultValue: 'Publishing {{version}} to {{env}}',
                version,
                env: environment,
              }) ?? undefined)
            : (t(copy.header.key, {
                defaultValue: copy.header.defaultValue,
              }) ?? undefined);

      // The in-flight status names the step the deploy is on, so it doubles as progress text.
      const text = step
        ? (t('pro-studio/navigation:publish_modal.toast.publishing_step', {
            defaultValue: '{{step}} — you can keep editing while it publishes.',
            step,
          }) ?? '')
        : (t(copy.text.key, {
            defaultValue: copy.text.defaultValue,
          }) ?? '');

      if (!isShowingProgressRef.current) {
        isShowingProgressRef.current = true;
        progressTextRef.current = text;
        // The button lock only. The draft is not being published — a snapshot of it is — so
        // the editor stays exactly as it was.
        tableStore.setSolutionVersionPublishingInProgress(true);
        sendUserMessage({
          type: 'info',
          header,
          text,
          toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
          autoClose: false,
        });
        return;
      }

      if (text !== progressTextRef.current) {
        progressTextRef.current = text;
        updateUserMessage({
          type: 'info',
          header,
          text,
          toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
          autoClose: false,
        });
      }
      return;
    }

    // The lock is released but the attempt's row has not arrived yet: wait for it rather
    // than read the release as a success (PSTUDIO-4333). A timer ends the wait, since a
    // publish that wrote no row at all pushes nothing more.
    if (phase === 'settling') {
      const releasedAt = releasedAtRef.current ?? now;
      const timer = setTimeout(
        () => setClockTick((tick) => tick + 1),
        Math.max(0, releasedAt + PUBLISH_OUTCOME_GRACE_MS - now)
      );
      return () => clearTimeout(timer);
    }

    // Only report an outcome for a publish this tab actually saw running. Without this, the
    // last (already finished, or long-failed) deployment row would be announced on every
    // mount — the same trap the old `sawPublishing` guard existed for.
    if (!isShowingProgressRef.current) return;

    // `idle` means no attempt is being followed and the solution is no longer PUBLISHING. Only
    // a solution this tab watched go PUBLISHING and come back to ACTIVE counts as a finished
    // publish — the pre-deployment-rows fallback, for a solution whose rows never arrive.
    if (
      phase === 'idle' &&
      !(sawPublishingRef.current && versionStatus === 'ACTIVE')
    ) {
      return;
    }
    sawPublishingRef.current = false;

    isShowingProgressRef.current = false;
    progressTextRef.current = null;

    const attemptId = buildPublishAttemptId({
      solutionId,
      publishStartedAt: publishStartedAtRef.current,
      deploymentId: deployment?.id,
      deployedAt: deployment?.deployedAt,
    });
    publishStartedAtRef.current = null;
    releasedAtRef.current = null;

    if (phase === 'stale') {
      // Not a failure: nothing says the deploy failed, only that it stopped reporting. The
      // backend has let the lock go, so the next publish is accepted.
      const message =
        t('pro-studio/navigation:publish_modal.toast.publish_stuck', {
          defaultValue:
            'This publish appears to be stuck — it has not reported any progress for a while. You can publish again.',
        }) ?? '';

      notifyPublishOutcome({ attemptId, outcome: 'stale', message });
      return;
    }

    if (phase === 'failed') {
      const message =
        (step && version && environment
          ? t('pro-studio/navigation:publish_modal.toast.publish_failed_step', {
              defaultValue:
                'Publishing {{version}} to {{env}} failed at “{{step}}”. Publish again to retry with your current changes.',
              version,
              env: environment,
              step,
            })
          : version && environment
            ? t('pro-studio/navigation:publish_modal.toast.publish_failed_to', {
                defaultValue:
                  'Publishing {{version}} to {{env}} failed. Publish again to retry with your current changes.',
                version,
                env: environment,
              })
            : t('pro-studio/navigation:publish_modal.toast.publish_failed', {
                defaultValue:
                  'There was an error publishing this solution. Please reach out to your Pypestream point of contact for assistance',
              })) ?? '';

      notifyPublishOutcome({ attemptId, outcome: 'failure', message });
      return;
    }

    const message =
      t(copy.success.key, {
        defaultValue: copy.success.defaultValue,
      }) ?? '';

    notifyPublishOutcome({ attemptId, outcome: 'success', message });
    // Refreshes the version list and the environment cards with the new slot. The draft's
    // identity does not move, so the table is not reloaded by this.
    sendProStudioEvent({ type: 'reloadCurrentBotData' });
  }, [
    deployments,
    solutionStatus,
    lockStartedAt,
    clockTick,
    solutionId,
    projectUsers,
    currentUserId,
    t,
  ]);
}
